import {
  type ContentsEntry,
  contentsOf,
  type InlineRun,
  type SectionLike,
} from '@app/studio'
import type { Entry } from '../../ui/Contents'

/**
 * An inline run as plain text, for a heading or a summary line.
 *
 * One family for the label: a part's name is as likely to be Hebrew as
 * English, and a contents holds no language runs to choose a face per word
 * from.
 */
export function plainText(run: InlineRun | undefined): string {
  if (!run) return ''
  return run.map((n) => (n.t === 'text' ? n.v : '')).join('')
}

/**
 * A book's parts, as the contents lists them.
 *
 * Shared by every surface that lists them — the reader's own book and a copy
 * read from a public link — because a contents that differed between the two
 * would be two answers to what the book contains. `contentsOf` honors each
 * run-in heading's `tocBehavior`, so a keyword inside prose stays out of both.
 */
export function contentsEntries(sections: readonly SectionLike[]): Entry[] {
  const toEntry = (e: ContentsEntry): Entry => {
    const title = plainText(e.title)
    /* A part whose title IS what the book calls it — a notes section titled
       "Notes" — says it once. Both lines carry the same word otherwise, and the
       second reads as a mistake rather than as a title. */
    const named = e.label && e.label.toLowerCase() !== title.toLowerCase()
    return {
      id: e.id,
      label: title,
      ...(named ? { name: e.label } : {}),
      children: e.children.map(toEntry),
    }
  }
  return contentsOf(sections).map(toEntry)
}
